import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Input, List, Typography, Space, Empty, Tag, Divider, Grid } from 'antd';
import {
  SearchOutlined,
  EnterOutlined,
  ClockCircleOutlined,
  StarFilled,
  RightOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useNavigation } from '@/contexts/NavigationContext';
import { useFavorites } from '@/hooks/useFavorites';
import { useAuthStore } from '@/store/authStore';
import { canAccessRouteByPermissions } from '@/config/routePermissions';
import { isPlatformSuperAdmin } from '@/utils/authUser';
import { navModuleKeyFromPath } from '@/config/navigationModules';
import { useVisibleNavigationModules } from '@/hooks/useVisibleNavigationModules';
import api from '@/services/api.service';
import './CommandPalette.module.css';

const { Text } = Typography;

interface MenuItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  group?: string;
  keywords?: string[];
}

// All available pages for search
const allPages: MenuItem[] = [
  // Core Operations
  { key: '/', label: 'Dashboard', group: 'Core', keywords: ['home', 'main'] },
  { key: '/help', label: 'Help Center', group: 'Core', keywords: ['help', 'how to', 'guide', 'manual', 'sop', 'workflow'] },
  { key: '/growth/leads', label: 'Leads', group: 'CRM & Growth', keywords: ['crm', 'customer', 'prospect'] },
  {
    key: '/growth/calendar',
    label: 'Sales calendar',
    group: 'CRM & Growth',
    keywords: ['calendar', 'reminder', 'task', 'follow up', 'schedule'],
  },
  {
    key: '/growth/activities',
    label: 'Daily Sales Activity Log',
    group: 'CRM & Growth',
    keywords: ['call', 'visit', 'meeting', 'activity', 'daily log'],
  },
  {
    key: '/growth/follow-ups',
    label: 'Follow-ups & Commitments',
    group: 'CRM & Growth',
    keywords: ['follow up', 'commitment', 'promise', 'reminder'],
  },
  {
    key: '/growth/standup',
    label: 'Daily Sales Stand-Up',
    group: 'CRM & Growth',
    keywords: ['standup', 'stand-up', 'daily brief', 'sales meeting'],
  },
  { key: '/growth/demos', label: 'Demos & Requirements', group: 'CRM & Growth', keywords: ['demo', 'poc', 'gap', 'fit'] },
  { key: '/growth/sales-targets', label: 'Sales Targets', group: 'CRM & Growth', keywords: ['target', 'quota', 'goal'] },
  {
    key: '/growth/reports/weekly-sales-performance',
    label: 'Weekly Sales Performance',
    group: 'CRM & Growth',
    keywords: ['weekly', 'performance', 'kpi', 'report'],
  },
  { key: '/crm/opportunities', label: 'Opportunities', group: 'CRM & Growth', keywords: ['sales', 'deals'] },
  { key: '/growth/quotations', label: 'Quotations', group: 'CRM & Growth', keywords: ['quotes', 'proposals'] },
  { key: '/growth/customers', label: 'Customers', group: 'CRM & Growth', keywords: ['clients'] },
  {
    key: '/epc/ipc',
    label: 'IPC register',
    group: 'EPC',
    keywords: ['ipc', 'interim payment', 'payment application', 'progress billing', 'quantity surveyor'],
  },
  {
    key: '/epc/ipc/certifications',
    label: 'IPC certifications',
    group: 'EPC',
    keywords: ['certification', 'certified', 'ipc', 'payment certificate'],
  },
  {
    key: '/cspm',
    label: 'Externally Owned Property',
    group: 'CRM & Growth',
    keywords: ['cspm', 'cpr', 'customer property', 'customer supplied', 'free issue', 'custody', 'externally owned'],
  },
  {
    key: '/cspm/receipts/new',
    label: 'New Externally Owned Property Receipt',
    group: 'CRM & Growth',
    keywords: ['cspm', 'cpr', 'receive', 'customer property', 'externally owned'],
  },
  {
    key: '/cspm/requirements/new',
    label: 'New Externally Owned Property Requirement',
    group: 'CRM & Growth',
    keywords: ['cspm', 'expected supply', 'requirement'],
  },
  {
    key: '/cspm/returns/new',
    label: 'New Externally Owned Property Return',
    group: 'CRM & Growth',
    keywords: ['cspm', 'return to customer'],
  },
  {
    key: '/cspm/reconciliations',
    label: 'Externally Owned Property Reconciliation',
    group: 'CRM & Growth',
    keywords: ['cspm', 'recon', 'custody close'],
  },
  {
    key: '/scm/reports/cspm-on-hand',
    label: 'Externally Owned Property On Hand',
    group: 'Inventory',
    keywords: ['cspm', 'customer stock', 'on hand'],
  },
  {
    key: '/scm/reports/cspm-awaiting-inspection',
    label: 'Externally Owned Property Awaiting Inspection',
    group: 'Inventory',
    keywords: ['cspm', 'quarantine', 'inspection'],
  },
  {
    key: '/scm/reports/cspm-exceptions',
    label: 'Externally Owned Property Exceptions',
    group: 'Inventory',
    keywords: ['cspm', 'damage', 'loss', 'ncr'],
  },
  {
    key: '/scm/reports/cspm-quality-hold',
    label: 'Externally Owned Property Quality Hold',
    group: 'Inventory',
    keywords: ['cspm', 'quarantine', 'hold'],
  },
  {
    key: '/mfg/operations',
    label: 'Operations Master',
    group: 'CRM & Growth',
    keywords: ['manufacturing operations', 'routing', 'bom', 'estimation'],
  },
  { key: '/growth/orders', label: 'Customer Orders', group: 'CRM & Growth', keywords: ['so', 'co', 'orders', 'sales order'] },
  {
    key: '/growth/order-timesheets',
    label: 'Timesheet for Rental',
    group: 'CRM & Growth',
    keywords: ['timesheet', 'rental', 'manpower', 'attendance'],
  },
  { key: '/growth/invoices', label: 'Tax Invoices', group: 'CRM & Growth', keywords: ['billing', 'invoice', 'sales invoice'] },
  
  // Estimations
  { key: '/crm/estimations', label: 'Estimations Dashboard', group: 'Cost Estimations', keywords: ['estimate', 'pricing'] },
  { key: '/estimation/boq', label: 'BOQ Estimations', group: 'Cost Estimations', keywords: ['boq', 'bill of quantities'] },
  { key: '/estimation/ai-predictor', label: 'AI Cost Predictor', group: 'Cost Estimations', keywords: ['ai', 'prediction', 'ml'] },
  { key: '/estimation/version-comparison', label: 'Version Comparison', group: 'Cost Estimations', keywords: ['compare', 'versions'] },
  { key: '/estimation/margin-rules', label: 'Margin Rules', group: 'Cost Estimations', keywords: ['margin', 'profit'] },
  { key: '/estimation/historical-analysis', label: 'Historical Analysis', group: 'Cost Estimations', keywords: ['history', 'trends'] },
  
  // Procurement
  { key: '/procurement/suppliers', label: 'Suppliers', group: 'Procurement', keywords: ['vendor'] },
  { key: '/procurement/requisitions', label: 'Purchase Requisitions', group: 'Procurement', keywords: ['pr', 'request'] },
  { key: '/procurement/rfqs', label: 'RFQs', group: 'Procurement', keywords: ['rfq', 'request for quote'] },
  { key: '/procurement/orders', label: 'Purchase Orders', group: 'Procurement', keywords: ['po', 'purchase'] },
  { key: '/procurement/grns', label: 'Goods Receipt', group: 'Procurement', keywords: ['grn', 'receiving'] },
  {
    key: '/scm/procurement/receiving-inspections',
    label: 'Receiving Inspections',
    group: 'Procurement',
    keywords: ['receiving', 'grn', 'qc', 'inspect'],
  },
  { key: '/procurement/supplier-invoices', label: 'Supplier Invoices', group: 'Procurement', keywords: ['purchase bill', 'supplier invoice', 'ap'] },
  { key: '/scm/procurement/purchase-returns', label: 'Purchase Returns', group: 'Procurement', keywords: ['purchase return', 'return', 'debit'] },
  { key: '/fin/debit-notes', label: 'Debit Notes', group: 'Finance', keywords: ['debit note', 'supplier debit', 'dn'] },
  
  // Inventory
  { key: '/inventory/items', label: 'Items', group: 'Inventory', keywords: ['products', 'materials', 'parts'] },
  {
    key: '/scm/inventory/item-categories',
    label: 'Item categories',
    group: 'Inventory',
    keywords: ['item category', 'master', 'scm', 'supply chain'],
  },
  {
    key: '/scm/inventory/uom-units',
    label: 'Units of measure (UOM)',
    group: 'Inventory',
    keywords: ['uom', 'unit of measure', 'pcs', 'kg', 'scm', 'supply chain', 'master'],
  },
  {
    key: '/scm/inventory/engineering',
    label: 'Engineering materials & weights',
    group: 'Inventory',
    keywords: ['density', 'grade', 'section weight', 'theoretical weight', 'kg/m', 'engineering', 'master'],
  },
  { key: '/inventory/warehouses', label: 'Warehouses', group: 'Inventory', keywords: ['locations', 'storage'] },
  { key: '/inventory/stock-summary', label: 'Stock Summary', group: 'Inventory', keywords: ['stock', 'inventory'] },
  { key: '/inventory/transfers', label: 'Stock Transfers', group: 'Inventory', keywords: ['move', 'transfer'] },
  { key: '/inventory/adjustments', label: 'Stock Adjustments', group: 'Inventory', keywords: ['adjust', 'correction'] },
  
  // Manufacturing
  { key: '/mfg/boms', label: 'Bill of Materials', group: 'Manufacturing', keywords: ['bom', 'recipe'] },
  { key: '/mfg/work-orders', label: 'Work Orders', group: 'Manufacturing', keywords: ['wo', 'production'] },
  {
    key: '/mfg/production-requests',
    label: 'Production requests',
    group: 'Manufacturing',
    keywords: ['mts', 'pr', 'stock', 'make to stock'],
  },
  {
    key: '/mfg/traceability-matrix',
    label: 'Traceability matrix',
    group: 'Manufacturing',
    keywords: ['heat', 'serial', 'mtc', 'trace'],
  },
  {
    key: '/mfg/work-order-operations',
    label: 'WO operations board',
    group: 'Manufacturing',
    keywords: ['execution', 'shop floor', 'stages', 'start complete'],
  },
  {
    key: '/mfg/work-order-operations/eod-close',
    label: 'End of day close',
    group: 'Manufacturing',
    keywords: ['eod', 'end of day', 'shop floor', 'batch close', 'pause complete'],
  },
  {
    key: '/mfg/stage-master',
    label: 'Stage Master',
    group: 'Manufacturing',
    keywords: ['work order stages', 'stage template', 'qc stages'],
  },
  { key: '/mfg/operations', label: 'Operations', group: 'Manufacturing', keywords: ['routing', 'process'] },
  { key: '/mfg/workstations', label: 'Workstations', group: 'Manufacturing', keywords: ['machines', 'stations'] },
  { key: '/mfg/shifts', label: 'Shifts', group: 'Manufacturing', keywords: ['schedule'] },
  { key: '/mfg/calendar', label: 'Production Calendar', group: 'Manufacturing', keywords: ['schedule', 'planning'] },
  { key: '/mfg/capacity-planning', label: 'Capacity Planning', group: 'Manufacturing', keywords: ['capacity', 'resources'] },
  { key: '/mfg/master-schedule', label: 'Master Schedule', group: 'Manufacturing', keywords: ['mps', 'planning', 'demand'] },
  { key: '/mfg/material-audit', label: 'Material Audit', group: 'Manufacturing', keywords: ['bom', 'shortage', 'stock'] },
  { key: '/mfg/job-scheduling', label: 'Job Scheduling', group: 'Manufacturing', keywords: ['jobs', 'scheduling'] },
  
  // Finance
  { key: '/fin/gl/chart-of-accounts', label: 'Chart of Accounts', group: 'Finance', keywords: ['coa', 'accounts'] },
  { key: '/fin/gl/journal-vouchers', label: 'Journal Vouchers', group: 'Finance', keywords: ['jv', 'journal'] },
  { key: '/fin/banking/bank-accounts', label: 'Bank Accounts', group: 'Finance', keywords: ['bank', 'cash'] },
  { key: '/fin/setup/currencies', label: 'Currencies', group: 'Finance', keywords: ['currency', 'exchange'] },
  {
    key: '/platform/foundation/revenue-service-types',
    label: 'Revenue service types',
    group: 'Finance',
    keywords: ['revenue', 'service type', 'sales gl', 'income account', 'foundation'],
  },
  { key: '/fin/reports', label: 'Financial Reports', group: 'Finance', keywords: ['reports', 'statements'] },
  
  // Quality
  { key: '/qms/dashboard', label: 'Quality Dashboard', group: 'Quality', keywords: ['quality', 'overview'] },
  { key: '/qms/quality-plans', label: 'Quality Plans', group: 'Quality', keywords: ['qp', 'plans'] },
  { key: '/qms/inspections', label: 'Stage Inspections', group: 'Quality', keywords: ['inspect', 'check', 'stage', 'line'] },
  {
    key: '/qms/receiving-dimensional-inspections',
    label: 'Receiving Dimension Inspections',
    group: 'Quality',
    keywords: ['receiving', 'dimension', 'grn', 'qc'],
  },
  {
    key: '/qms/certificates-of-conformity',
    label: 'Certificate of Conformity',
    group: 'Quality',
    keywords: ['coc', 'conformity', 'certificate', 'quality form'],
  },
  {
    key: '/qms/tmmde',
    label: 'Equipment List',
    group: 'Quality',
    keywords: ['equipment', 'calibration', 'tmmde', 'measuring', 'instrument', 'gauge', 'serial', 'not for use'],
  },
  {
    key: '/qms/tmmde/impact',
    label: 'Equipment impact assessments',
    group: 'Quality',
    keywords: ['impact', 'out of calibration', 'tmmde', 'measuring', 'equipment', 'lost', 'damaged', 'customer notification'],
  },
  { key: '/qms/ncrs', label: 'NCRs', group: 'Quality', keywords: ['ncr', 'non-conformance'] },
  {
    key: '/qms/management-reviews',
    label: 'Management Review Meetings',
    group: 'Quality',
    keywords: ['mrm', 'management review', 'agenda', 'minutes', 'qms'],
  },
  {
    key: '/qms/document-management',
    label: 'Document Management',
    group: 'Quality',
    keywords: ['document control', 'controlled document', 'qsp', 'qsf', 'external documents', 'qms'],
  },
  { key: '/qms/capas', label: 'CAPAs', group: 'Quality', keywords: ['capa', 'corrective', 'preventive'] },
  { key: '/qms/audits', label: 'Audits', group: 'Quality', keywords: ['audit', 'review'] },
  { key: '/qms/kpis', label: 'Department KPIs', group: 'Quality', keywords: ['kpi', 'metrics'] },
  { key: '/qms/kpi-measurements', label: 'KPI Measurements', group: 'Quality', keywords: ['measurements', 'tracking'] },
  
  // Risk & Compliance
  { key: '/grc/risk-assessments', label: 'Risk Assessments', group: 'Risk & Compliance', keywords: ['risk', 'assessment', 'contingency', 'qms'] },
  { key: '/grc/competency', label: 'Competency & Training', group: 'Risk & Compliance', keywords: ['competency', 'training', 'skills'] },
  { key: '/grc/competency/job-profiles', label: 'Job Descriptions', group: 'Risk & Compliance', keywords: ['job description', 'jd', 'role profile', 'competency'] },
  { key: '/grc/competency/matrix', label: 'Competency Gap Matrix', group: 'Risk & Compliance', keywords: ['gap', 'matrix', 'competency', 'skills'] },
  { key: '/grc/competency/courses', label: 'Training Courses', group: 'Risk & Compliance', keywords: ['course', 'training', 'catalogue'] },
  { key: '/grc/competency/schedule', label: 'Annual Training Schedule', group: 'Risk & Compliance', keywords: ['training plan', 'schedule', 'tna'] },
  { key: '/grc/competency/sessions', label: 'Training Attendance', group: 'Risk & Compliance', keywords: ['attendance', 'training session'] },
  { key: '/grc/competency/effectiveness', label: 'Training Effectiveness', group: 'Risk & Compliance', keywords: ['effectiveness', 'evaluation', 'training'] },
  { key: '/grc/competency/assessments', label: 'Competency Assessments', group: 'Risk & Compliance', keywords: ['assessment', 'probation', 'appraisal', 'competency'] },
  { key: '/grc/competency/import', label: 'Competency Import', group: 'Risk & Compliance', keywords: ['import', 'bulk', 'competency', 'training'] },
  { key: '/grc/calibration', label: 'Calibration', group: 'Risk & Compliance', keywords: ['calibration', 'equipment', 'certificate'] },
  { key: '/compliance/uae-einvoicing', label: 'UAE E-Invoicing', group: 'Risk & Compliance', keywords: ['uae', 'einvoicing'] },
  { key: '/compliance/ksa-zatca', label: 'KSA ZATCA', group: 'Risk & Compliance', keywords: ['ksa', 'zatca', 'saudi'] },
  
  // Projects & HR
  { key: '/projects/list', label: 'Projects', group: 'Projects & HR', keywords: ['projects', 'tasks'] },
  { key: '/projects/timesheets', label: 'Timesheets', group: 'Projects & HR', keywords: ['time', 'hours'] },
  { key: '/projects/costs', label: 'Project Costs', group: 'Projects & HR', keywords: ['costs', 'expenses'] },
  { key: '/projects/reports', label: 'Project Reports', group: 'Projects & HR', keywords: ['reports'] },
  { key: '/hr/dashboard', label: 'HRMS Control Tower', group: 'Projects & HR', keywords: ['hr', 'hrms', 'dashboard', 'overview'] },
  { key: '/hr/employees', label: 'Employees', group: 'Projects & HR', keywords: ['staff', 'personnel'] },
  { key: '/hr/attendance', label: 'Attendance', group: 'Projects & HR', keywords: ['attendance', 'presence'] },
  { key: '/hr/leaves', label: 'Leave Management', group: 'Projects & HR', keywords: ['leave', 'vacation', 'pto'] },
  { key: '/hr/accruals', label: 'Accrual Management', group: 'Projects & HR', keywords: ['accrual', 'leave accrual', 'eosb', 'gratuity'] },
  { key: '/hr/payroll', label: 'Payroll', group: 'Projects & HR', keywords: ['payroll', 'salary'] },
  
  // Settings
  { key: '/settings/company', label: 'Company Settings', group: 'Settings', keywords: ['company', 'profile'] },
  { key: '/settings/users', label: 'Users & Roles', group: 'Settings', keywords: ['users', 'permissions', 'roles'] },
  { key: '/settings/system', label: 'System Configuration', group: 'Settings', keywords: ['system', 'config'] },
  { key: '/platform/setup/api-keys', label: 'API Keys', group: 'Settings', keywords: ['api', 'integration', 'key', 'external'] },
];

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { recentPages } = useNavigation();
  const { favorites } = useFavorites();
  const user = useAuthStore((s) => s.user);
  const superAdmin = isPlatformSuperAdmin(user);
  const { isModuleVisible } = useVisibleNavigationModules();
  const canAccessPath = (path: string) =>
    canAccessRouteByPermissions(path, user?.permissions ?? [], superAdmin);
  const canShowPage = (path: string) => {
    if (!canAccessPath(path)) return false;
    const mod = navModuleKeyFromPath(path);
    if (!mod) return true;
    return isModuleVisible(mod);
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const inputRef = useRef<any>(null);

  // Filter pages based on search query
  const filteredPages = useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const query = searchQuery.toLowerCase();
    return allPages.filter(page => {
      if (!canShowPage(page.key)) return false;
      const labelMatch = page.label.toLowerCase().includes(query);
      const groupMatch = page.group?.toLowerCase().includes(query);
      const keywordsMatch = page.keywords?.some(kw => kw.includes(query));
      
      return labelMatch || groupMatch || keywordsMatch;
    });
  }, [searchQuery, user?.permissions, superAdmin, isModuleVisible]);

  // Get favorite pages
  const favoritePages = useMemo(() => {
    return allPages.filter(
      page => favorites.includes(page.key) && canShowPage(page.key)
    );
  }, [favorites, user?.permissions, superAdmin, isModuleVisible]);

  const docSearchQ = useQuery({
    queryKey: ['erpx-search', searchQuery],
    queryFn: async () => {
      const body = (await api.get('/readiness/search', { params: { q: searchQuery } })) as {
        data?: { results?: Array<{ type: string; id: string; label: string; path?: string }> };
      };
      return body.data?.results || [];
    },
    enabled: open && searchQuery.trim().length >= 2,
    staleTime: 15_000,
    retry: false,
  });

  // Get items to display
  const displayItems = useMemo(() => {
    if (searchQuery.trim()) {
      const docs: MenuItem[] = (docSearchQ.data || []).map((r) => ({
        key: r.path || `doc:${r.type}:${r.id}`,
        label: `${r.label} (${r.type})`,
        group: 'Documents',
        keywords: [r.type],
      }));
      return [...filteredPages, ...docs];
    }
    
    // Show recents and favorites when no search
    const items: MenuItem[] = [];
    if (recentPages.length > 0) {
      items.push(
        ...recentPages
          .filter((page) => canAccessPath(page.path))
          .map(page => ({
            key: page.path,
            label: page.title,
            group: 'Recent',
          }))
      );
    }
    if (favoritePages.length > 0) {
      items.push(...favoritePages.map(page => ({
        ...page,
        group: 'Favorites',
      })));
    }
    return items;
  }, [searchQuery, filteredPages, recentPages, favoritePages, user?.permissions, superAdmin, docSearchQ.data]);

  // Reset selected index when items change
  useEffect(() => {
    setSelectedIndex(0);
  }, [displayItems]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      setSearchQuery('');
      setSelectedIndex(0);
    }
  }, [open]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, displayItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (displayItems[selectedIndex]) {
          handleNavigate(displayItems[selectedIndex].key);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, selectedIndex, displayItems]);

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  // Group items
  const groupedItems = useMemo(() => {
    const groups: Record<string, MenuItem[]> = {};
    displayItems.forEach(item => {
      const group = item.group || 'Other';
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(item);
    });
    return groups;
  }, [displayItems]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closeIcon={null}
      width={isMobile ? 'calc(100vw - 16px)' : 600}
      centered
      className="command-palette-modal"
      title={null}
      aria-label="Command palette"
      styles={{
        body: { padding: 0 },
        content: { maxWidth: isMobile ? 'calc(100vw - 16px)' : 600 },
      }}
    >
      <div className="command-palette">
        {/* Search Input */}
        <div style={{ padding: isMobile ? '12px' : '16px', borderBottom: '1px solid #f0f0f0' }}>
          <Input
            ref={inputRef}
            size={isMobile ? 'middle' : 'large'}
            placeholder={isMobile ? 'Search...' : 'Search pages... (type to filter)'}
            prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bordered={false}
            style={{ fontSize: isMobile ? '14px' : '16px' }}
          />
        </div>

        {/* Results */}
        <div style={{ maxHeight: isMobile ? '300px' : '400px', overflowY: 'auto' }}>
          {displayItems.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                searchQuery ? 'No pages found' : 'Start typing to search...'
              }
              style={{ padding: '40px 20px' }}
            />
          ) : (
            Object.entries(groupedItems).map(([group, items], groupIndex) => (
              <div key={group}>
                {groupIndex > 0 && <Divider style={{ margin: '8px 0' }} />}
                <div style={{ padding: '8px 16px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase' }}>
                    {group}
                  </Text>
                </div>
                <List
                  size="small"
                  dataSource={items}
                  renderItem={(item, index) => {
                    const globalIndex = displayItems.findIndex(di => di.key === item.key);
                    const isSelected = globalIndex === selectedIndex;
                    
                    return (
                      <List.Item
                        onClick={() => handleNavigate(item.key)}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          background: isSelected ? '#f0f5ff' : 'transparent',
                          borderLeft: isSelected ? '3px solid #145A54' : '3px solid transparent',
                        }}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                      >
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                          <Space>
                            {item.group === 'Recent' && <ClockCircleOutlined style={{ color: '#8c8c8c' }} />}
                            {item.group === 'Favorites' && <StarFilled style={{ color: '#faad14' }} />}
                            <Text>{item.label}</Text>
                          </Space>
                          {isSelected && <EnterOutlined style={{ color: '#8c8c8c' }} />}
                        </Space>
                      </List.Item>
                    );
                  }}
                />
              </div>
            ))
          )}
        </div>

        {/* Footer Hints */}
        {!isMobile && (
          <div
            style={{
              padding: '8px 16px',
              borderTop: '1px solid #f0f0f0',
              background: '#fafafa',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Space size="large">
              <Space size="small">
                <Tag style={{ margin: 0 }}>↑↓</Tag>
                <Text type="secondary" style={{ fontSize: '12px' }}>Navigate</Text>
              </Space>
              <Space size="small">
                <Tag style={{ margin: 0 }}>↵</Tag>
                <Text type="secondary" style={{ fontSize: '12px' }}>Select</Text>
              </Space>
              <Space size="small">
                <Tag style={{ margin: 0 }}>Esc</Tag>
                <Text type="secondary" style={{ fontSize: '12px' }}>Close</Text>
              </Space>
            </Space>
          </div>
        )}
      </div>
    </Modal>
  );
};

